import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import {
  ArrowLeft, Flag, Trash2, Eye, EyeOff, CheckCircle, XCircle, Search,
  MessageSquare, ChevronLeft, ChevronRight, Loader2, MapPin, AlertTriangle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminConnectModeration = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('posts');
  const [posts, setPosts] = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');

  const token = localStorage.getItem('adminToken');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { if (!token) navigate('/admin/login'); }, [token]);

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/connect/posts?page=${page}&per_page=20`, { headers });
      setPosts(res.data.posts || []);
      setTotalPages(res.data.total_pages || 1);
    } catch { toast.error('Failed to load posts'); }
    finally { setLoading(false); }
  }, [page]);

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/connect/reports`, { headers });
      setReports(res.data || []);
    } catch { toast.error('Failed to load reports'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (activeTab === 'posts') fetchPosts();
    else fetchReports();
  }, [activeTab, fetchPosts, fetchReports]);

  const removePost = async (postId) => {
    if (!window.confirm('Remove this post permanently?')) return;
    try {
      await axios.delete(`${API}/admin/connect/posts/${postId}`, { headers });
      toast.success('Post removed');
      fetchPosts();
      fetchReports();
    } catch { toast.error('Failed'); }
  };

  const togglePostStatus = async (postId, newStatus) => {
    try {
      await axios.put(`${API}/admin/connect/posts/${postId}`, { status: newStatus }, { headers });
      toast.success(`Post ${newStatus}`);
      fetchPosts();
    } catch { toast.error('Failed'); }
  };

  const dismissReport = async (reportId) => {
    try {
      await axios.put(`${API}/admin/connect/reports/${reportId}`, { status: 'dismissed' }, { headers });
      toast.success('Report dismissed');
      fetchReports();
    } catch { toast.error('Failed'); }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';

  const filteredPosts = searchQuery
    ? posts.filter(p => p.title?.toLowerCase().includes(searchQuery.toLowerCase()) || p.author_name?.toLowerCase().includes(searchQuery.toLowerCase()))
    : posts;

  return (
    <div className="min-h-screen bg-stone-50 p-4 md:p-8">
      <div className="max-w-5xl mx-auto">
        <Button variant="ghost" onClick={() => navigate('/admin/dashboard')} className="mb-4 text-stone-600 hover:text-[#1A4D2E]">
          <ArrowLeft className="mr-2" size={20} /> Back to Dashboard
        </Button>

        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-[#1A4D2E] mb-1" style={{ fontFamily: '"Playfair Display", serif' }}>
            Albriks Connect — Moderation
          </h1>
          <p className="text-stone-500 text-sm">Manage forum posts and review reported content</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button onClick={() => setActiveTab('posts')} data-testid="tab-all-posts"
            className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all ${activeTab === 'posts' ? 'bg-[#1A4D2E] text-white' : 'bg-white text-stone-600 border hover:border-[#1A4D2E]'}`}>
            <MessageSquare size={14} className="inline mr-1.5 -mt-0.5" /> All Posts
          </button>
          <button onClick={() => setActiveTab('reports')} data-testid="tab-reports"
            className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all ${activeTab === 'reports' ? 'bg-red-600 text-white' : 'bg-white text-stone-600 border hover:border-red-500'}`}>
            <Flag size={14} className="inline mr-1.5 -mt-0.5" /> Reported ({reports.length})
          </button>
        </div>

        {/* All Posts Tab */}
        {activeTab === 'posts' && (
          <>
            <div className="relative mb-4">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
              <Input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search posts by title or author..." className="pl-9" />
            </div>

            {loading ? (
              <div className="text-center py-16"><Loader2 size={28} className="animate-spin mx-auto text-stone-400" /></div>
            ) : filteredPosts.length === 0 ? (
              <Card className="p-12 text-center"><p className="text-stone-500">No posts found</p></Card>
            ) : (
              <div className="space-y-3">
                {filteredPosts.map(post => (
                  <Card key={post.id} className="bg-white p-4 rounded-xl" data-testid={`admin-post-${post.id}`}>
                    <div className="flex items-start gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <h3 className="font-bold text-stone-900 text-sm">{post.title}</h3>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            post.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                          }`}>{post.status}</span>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            post.visibility === 'public' ? 'bg-stone-100 text-stone-600' :
                            post.visibility === 'agents_only' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'
                          }`}>{post.visibility}</span>
                          {post.reports_count > 0 && (
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                              <Flag size={10} className="inline mr-0.5" />{post.reports_count} reports
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-stone-500">
                          by {post.author_name} ({post.author_role}) &middot; {post.category} &middot; {formatDate(post.created_at)}
                          {post.city && <> &middot; <MapPin size={10} className="inline" /> {post.city}</>}
                        </p>
                        <p className="text-sm text-stone-600 mt-1 line-clamp-2">{post.description}</p>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {post.status === 'active' ? (
                          <Button size="sm" variant="outline" onClick={() => togglePostStatus(post.id, 'removed')} className="text-orange-600 border-orange-300 hover:bg-orange-50 h-8 px-2">
                            <EyeOff size={14} />
                          </Button>
                        ) : (
                          <Button size="sm" variant="outline" onClick={() => togglePostStatus(post.id, 'active')} className="text-green-600 border-green-300 hover:bg-green-50 h-8 px-2">
                            <Eye size={14} />
                          </Button>
                        )}
                        <Button size="sm" variant="outline" onClick={() => removePost(post.id)} className="text-red-600 border-red-300 hover:bg-red-50 h-8 px-2">
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))}

                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-4">
                    <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="rounded-full"><ChevronLeft size={16} /></Button>
                    <span className="text-sm text-stone-600 px-2">Page {page} of {totalPages}</span>
                    <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="rounded-full"><ChevronRight size={16} /></Button>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* Reports Tab */}
        {activeTab === 'reports' && (
          <>
            {loading ? (
              <div className="text-center py-16"><Loader2 size={28} className="animate-spin mx-auto text-stone-400" /></div>
            ) : reports.length === 0 ? (
              <Card className="p-12 text-center">
                <CheckCircle size={48} className="mx-auto mb-4 text-green-300" />
                <p className="text-lg text-stone-600">No pending reports</p>
                <p className="text-sm text-stone-400 mt-1">All clear — no posts have been flagged</p>
              </Card>
            ) : (
              <div className="space-y-3">
                {reports.map(report => (
                  <Card key={report.id} className="bg-white p-4 rounded-xl border-l-4 border-l-red-400" data-testid={`report-${report.id}`}>
                    <div className="flex items-start gap-4">
                      <AlertTriangle size={20} className="text-red-500 mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-stone-900 text-sm">{report.post_title || 'Unknown Post'}</h3>
                        <p className="text-xs text-stone-500 mt-0.5">
                          Post by {report.post_author || '?'} &middot; Reported by {report.user_name} &middot; {formatDate(report.created_at)}
                        </p>
                        <div className="mt-2 p-2.5 bg-red-50 rounded-lg border border-red-100">
                          <p className="text-sm text-red-800"><strong>Reason:</strong> {report.reason}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <Button size="sm" variant="outline" onClick={() => removePost(report.post_id)} className="text-red-600 border-red-300 hover:bg-red-50 h-8 px-2" title="Remove post">
                          <Trash2 size={14} />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => dismissReport(report.id)} className="text-green-600 border-green-300 hover:bg-green-50 h-8 px-2" title="Dismiss report">
                          <CheckCircle size={14} />
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AdminConnectModeration;
